/**
 * Engine notification composition root.
 *
 * Each domain owns its own routing state and disposer. This module intentionally
 * contains no event behavior; it only wires store capabilities into registrars.
 */

import type { StoreApi } from "zustand";
import { registerDocumentNotificationHandlers } from "./document-notification-handlers";
import { registerExecutionNotificationHandlers } from "./execution-notification-handlers";
import { routeBackgroundExecution as projectBackgroundExecution } from "./execution-notification-projection";
import type { getNotificationBus } from "./notification-bus";
import type { PluginExecutionState } from "./plugin-execution-store";
import { registerPublishNotificationHandlers } from "./publish-notification-handlers";
import { registerServiceNotificationHandlers } from "./service-notification-handlers";

type ExecStore = Pick<StoreApi<PluginExecutionState>, "getState" | "setState">;

export type NotificationHandlerDependencies = Pick<
  typeof import("./plugin-execution-store"),
  | "_syncBackgroundRunningTabs"
  | "getActiveTabId"
  | "reconcileEngineTabContent"
  | "resyncExternalConfigFile"
  | "resyncExecutionSessionProjection"
>;

export { EXECUTION_NOTIFICATION_BATCH_MS } from "./execution-notification-handlers";

/** @deprecated Import from execution-notification-projection for new code. */
export function routeBackgroundExecution(
  store: ExecStore,
  dependencies: NotificationHandlerDependencies,
  method: string,
  params: unknown,
): void {
  projectBackgroundExecution(
    store,
    dependencies._syncBackgroundRunningTabs,
    method,
    params,
  );
}

export function registerNotificationHandlers(
  bus: ReturnType<typeof getNotificationBus>,
  store: ExecStore,
  dependencies: NotificationHandlerDependencies,
): () => void {
  const disposers = [
    registerExecutionNotificationHandlers(bus, store, {
      syncBackgroundRunningTabs: dependencies._syncBackgroundRunningTabs,
      getActiveTabId: dependencies.getActiveTabId,
    }),
    registerDocumentNotificationHandlers(bus, {
      reconcileEngineTabContent: dependencies.reconcileEngineTabContent,
      resyncExternalConfigFile: dependencies.resyncExternalConfigFile,
    }),
    registerServiceNotificationHandlers(bus, store),
    registerPublishNotificationHandlers(bus, store, {
      resyncExecutionSessionProjection:
        dependencies.resyncExecutionSessionProjection,
    }),
  ];

  return () => {
    for (const dispose of disposers.splice(0)) dispose();
  };
}
